import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { jsPDF } from "jspdf";

import { supabase } from "../lib/supabase";
import { updateProject } from "../api/projects";
import { createTask, getTasksForProject, deleteTask, updateTask } from "../api/tasks";
import { getExpensesForProject, createExpense, deleteExpense } from "../api/expenses";
import { getNotesForProject, createNote, updateNote, deleteNote } from "../api/notes";

export default function ProjectDetails() {
  const { id } = useParams();
  const projectId = id;

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);

  const [newTask, setNewTask] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Expenses modal
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [expenseRows, setExpenseRows] = useState([]);
  const [expenseLoading, setExpenseLoading] = useState(false);

  const [expenseForm, setExpenseForm] = useState({
    vendor: "",
    description: "",
    amount: "",
    expense_date: new Date().toISOString().slice(0, 10),
  });

  // Notes
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);

  // "Åben" note editor (ny note)
  const [noteDraft, setNoteDraft] = useState({
    title: "Byggemøde",
    note_date: new Date().toISOString().slice(0, 10),
    body: "",
  });

  // Inline edit state for note in list
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: "", note_date: "", body: "" });
  const [savingNoteId, setSavingNoteId] = useState(null);

  async function loadProjectAndTasks() {
    if (!projectId) return;

    setErrorMsg("");
    setLoading(true);

    try {
      const { data: p, error: pErr } = await supabase
        .from("projects")
        .select("id, name, status, phase, budget, expenses, offer_amount, created_at")
        .eq("id", projectId)
        .single();

      if (pErr) throw pErr;
      setProject(p);

      const t = await getTasksForProject(projectId);
      setTasks(t);
    } catch (err) {
      console.error("loadProjectAndTasks error:", err);
      setErrorMsg(err?.message || "Kunne ikke hente projektet.");
      setProject(null);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadExpenses() {
    if (!projectId) return;

    setExpenseLoading(true);
    setErrorMsg("");

    try {
      const rows = await getExpensesForProject(projectId);
      setExpenseRows(rows);
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || "Kunne ikke hente omkostninger.");
    } finally {
      setExpenseLoading(false);
    }
  }

  async function loadNotes() {
    if (!projectId) return;

    setNotesLoading(true);
    setErrorMsg("");

    try {
      const rows = await getNotesForProject(projectId);
      setNotes(rows);
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || "Kunne ikke hente noter.");
    } finally {
      setNotesLoading(false);
    }
  }

  // mini-funktion: luk modal + refresh totals
  async function closeExpensesModal() {
    setExpensesOpen(false);
    await loadExpenses();
    await loadProjectAndTasks();
  }

  useEffect(() => {
    loadProjectAndTasks();
    loadExpenses();
    loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Totals før profit/margin
  const expensesTotal = useMemo(() => {
    return expenseRows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  }, [expenseRows]);

  const budget = project?.budget ?? 0;
  const expenses = expensesTotal;
  const profit = budget - expenses;

  const offerAmount = project?.offer_amount ?? 0;
  const marginPct = useMemo(() => {
    const offer = Number(offerAmount || 0);
    const exp = Number(expenses || 0);
    if (!offer || offer <= 0) return 0;
    return Math.round(((offer - exp) / offer) * 100);
  }, [offerAmount, expenses]);

  const projectProgressPct = useMemo(() => {
    if (!tasks.length) return 0;
    const sum = tasks.reduce((acc, t) => acc + (t.progress_pct ?? 0), 0);
    return Math.round(sum / tasks.length);
  }, [tasks]);

  // -------- PDF EXPORT (locked notes) ----------
  function exportNoteToPDF(note) {
    const doc = new jsPDF({ unit: "mm", format: "a4" });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    const maxWidth = pageWidth - margin * 2;

    const projectName = project?.name || "Projekt";
    const title = note.title || "Note";
    const noteDate = note.note_date || "";
    const created = note.created_at ? new Date(note.created_at).toLocaleString() : "";

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(projectName, margin, 18);

    doc.setFontSize(12);
    doc.text(title, margin, 26);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Dato: ${noteDate}`, margin, 32);
    if (created) doc.text(`Oprettet: ${created}`, margin, 37);

    doc.setFontSize(11);
    const bodyText = (note.body || "").toString();
    const lines = doc.splitTextToSize(bodyText, maxWidth);

    let y = 46;
    const lineHeight = 6;

    lines.forEach((line) => {
      if (y > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    });

    const safeTitle = (title || "note")
      .replace(/[^\w\- ]+/g, "")
      .trim()
      .replace(/\s+/g, "_");

    const safeProject = (projectName || "projekt").replace(/[^\w\- ]+/g, "").trim().replace(/\s+/g, "_");

    doc.save(`${safeProject}_${safeTitle}.pdf`);
  }

  // Tasks
  const addTask = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    const title = newTask.trim();
    if (!title) return;

    try {
      await createTask(projectId, null, title);
      setNewTask("");
      await loadProjectAndTasks();
    } catch (err) {
      console.error("createTask error:", err);
      setErrorMsg(err?.message || "Kunne ikke oprette opgave.");
    }
  };

  const handleDeleteTask = async (taskId) => {
    setErrorMsg("");
    try {
      await deleteTask(taskId);
      await loadProjectAndTasks();
    } catch (err) {
      console.error("deleteTask error:", err);
      setErrorMsg(err?.message || "Kunne ikke slette opgave.");
    }
  };

  // Expenses
  async function addExpense(e) {
    e.preventDefault();
    setErrorMsg("");

    try {
      await createExpense(projectId, {
        vendor: expenseForm.vendor,
        description: expenseForm.description,
        amount: expenseForm.amount,
        expense_date: expenseForm.expense_date,
      });

      setExpenseForm((p) => ({ ...p, description: "", amount: "" }));
      await loadExpenses();
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || "Kunne ikke tilføje omkostning.");
    }
  }

  async function removeExpense(expenseId) {
    setErrorMsg("");
    try {
      await deleteExpense(expenseId);
      await loadExpenses();
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || "Kunne ikke slette omkostning.");
    }
  }

  // Notes: Ny note -> gem til liste
  async function saveNewNote({ lockAfter = false } = {}) {
    setErrorMsg("");
    const body = (noteDraft.body || "").trim();
    if (!body) return;

    try {
      await createNote(projectId, {
        title: noteDraft.title,
        body,
        note_date: noteDraft.note_date,
        locked: lockAfter,
      });

      setNoteDraft((p) => ({ ...p, body: "" }));
      await loadNotes();
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || "Kunne ikke gemme note.");
    }
  }

  function startEditNote(note) {
    if (note.locked) return;
    setEditingId(note.id);
    setEditDraft({
      title: note.title || "",
      note_date: note.note_date || new Date().toISOString().slice(0, 10),
      body: note.body || "",
    });
  }

  async function saveEditNote(noteId) {
    setErrorMsg("");
    setSavingNoteId(noteId);

    try {
      const updated = await updateNote(noteId, {
        title: editDraft.title || null,
        note_date: editDraft.note_date || null,
        body: editDraft.body,
      });

      setNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)));
      setEditingId(null);
      setEditDraft({ title: "", note_date: "", body: "" });
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || "Kunne ikke gemme ændringer.");
    } finally {
      setSavingNoteId(null);
    }
  }

  async function lockNote(noteId) {
    setErrorMsg("");
    setSavingNoteId(noteId);

    try {
      const updated = await updateNote(noteId, { locked: true });
      setNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)));

      if (editingId === noteId) {
        setEditingId(null);
        setEditDraft({ title: "", note_date: "", body: "" });
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || "Kunne ikke låse note.");
    } finally {
      setSavingNoteId(null);
    }
  }

  async function removeNote(noteId) {
    setErrorMsg("");
    try {
      await deleteNote(noteId);
      await loadNotes();
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || "Kunne ikke slette note.");
    }
  }

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-sm">
        <Link to="/projects" className="text-blue-600 hover:underline text-sm mb-4 inline-block">
          ← Tilbage til projekter
        </Link>
        <p className="text-gray-500">Henter projekt...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-sm">
        <Link to="/projects" className="text-blue-600 hover:underline text-sm mb-4 inline-block">
          ← Tilbage til projekter
        </Link>

        <h1 className="text-2xl font-bold mb-2 text-gray-800">Projekt ikke fundet</h1>
        <p className="text-gray-600">Enten findes projektet ikke, eller du har ikke adgang.</p>

        {errorMsg ? (
          <div className="mt-4 p-3 rounded bg-red-50 text-red-700 text-sm border border-red-200">
            {errorMsg}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      <Link to="/projects" className="text-blue-600 hover:underline text-sm mb-4 inline-block">
        ← Tilbage til projekter
      </Link>

      <div className="flex items-start justify-between gap-4 mb-4">
        <h1 className="text-2xl font-bold text-gray-800">{project.name}</h1>

        <div className="text-right">
          <div className="text-sm text-gray-500">Samlet fremdrift</div>
          <div className="text-2xl font-bold">{projectProgressPct}%</div>
        </div>
      </div>

      {errorMsg ? (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm border border-red-200">
          {errorMsg}
        </div>
      ) : null}

      {/* Stamdata */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {/* Projektinfo */}
        <div className="border rounded p-4 bg-gray-50">
          <h2 className="font-semibold text-lg text-gray-700 mb-2">Projektinfo</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <div>
              <div className="text-sm text-gray-600 mb-1"><strong>Status</strong></div>
              <select
                value={project.status}
                onChange={async (e) => {
                  const next = e.target.value;
                  setProject((p) => ({ ...p, status: next }));
                  try {
                    const updated = await updateProject(project.id, { status: next });
                    setProject(updated);
                  } catch (err) {
                    console.error(err);
                    setErrorMsg(err?.message || "Kunne ikke opdatere status");
                    await loadProjectAndTasks();
                  }
                }}
                className="border p-2 rounded w-full"
              >
                <option value="planlagt">Planlagt</option>
                <option value="igangværende">I gang</option>
                <option value="afsluttet">Afsluttet</option>
              </select>
            </div>

            <div>
              <div className="text-sm text-gray-600 mb-1"><strong>Fase</strong></div>
              <select
                value={project.phase ?? "design"}
                onChange={async (e) => {
                  const next = e.target.value;
                  setProject((p) => ({ ...p, phase: next }));
                  try {
                    const updated = await updateProject(project.id, { phase: next });
                    setProject(updated);
                  } catch (err) {
                    console.error(err);
                    setErrorMsg(err?.message || "Kunne ikke opdatere fase");
                    await loadProjectAndTasks();
                  }
                }}
                className="border p-2 rounded w-full"
              >
                <option value="design">Design</option>
                <option value="byggeri">Byggeri</option>
                <option value="afslutning">Afslutning</option>
              </select>
            </div>
          </div>

          <p className="mt-3"><strong>Oprettet:</strong> {project.created_at ? new Date(project.created_at).toLocaleString() : ""}</p>
          <p className="mt-3 text-sm text-gray-500">
            Projekt ID: <span className="font-mono">{project.id}</span>
          </p>
        </div>

        {/* Økonomi */}
        <div className="border rounded p-4 bg-gray-50">
          <h2 className="font-semibold text-lg text-gray-700 mb-2">Økonomi</h2>

          <p><strong>Budget:</strong> {Number(budget).toLocaleString()} kr.</p>

          <button
            type="button"
            onClick={async () => {
              setExpensesOpen(true);
              await loadExpenses();
            }}
            className="text-left w-full mt-2"
          >
            <p>
              <strong>Udgifter:</strong>{" "}
              <span className="text-blue-600 hover:underline">
                {Number(expensesTotal).toLocaleString()} kr.
              </span>
            </p>
            <p className="text-xs text-gray-500">Klik for at registrere omkostninger</p>
          </button>

          <p className="mt-2">
            <strong>Fortjeneste:</strong>{" "}
            <span className={`font-bold ${profit >= 0 ? "text-green-600" : "text-red-600"}`}>
              {Number(profit).toLocaleString()} kr.
            </span>
          </p>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-sm text-gray-600 mb-1"><strong>Tilbudssum</strong></div>
              <input
                type="number"
                value={project.offer_amount ?? ""}
                onChange={(e) => {
                  const v = e.target.value === "" ? null : Number(e.target.value);
                  setProject((p) => ({ ...p, offer_amount: v }));
                }}
                onBlur={async () => {
                  try {
                    const updated = await updateProject(project.id, { offer_amount: project.offer_amount });
                    setProject(updated);
                  } catch (err) {
                    console.error(err);
                    setErrorMsg(err?.message || "Kunne ikke gemme tilbudssum");
                    await loadProjectAndTasks();
                  }
                }}
                className="border p-2 rounded w-full"
                placeholder="fx 250000"
              />
              <div className="text-xs text-gray-500 mt-1">Gemmes når du klikker væk fra feltet.</div>
            </div>

            <div>
              <div className="text-sm text-gray-600 mb-1"><strong>Dækningsgrad</strong></div>
              <div className="border p-2 rounded bg-white w-full">
                <span className={`font-bold ${marginPct >= 0 ? "text-green-700" : "text-red-700"}`}>
                  {marginPct}%
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">Beregnes ud fra Tilbudssum og Udgifter.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tasks */}
      <div className="border rounded p-4 bg-gray-50">
        <h2 className="font-semibold text-lg text-gray-700 mb-4">Opgaver</h2>

        <form onSubmit={addTask} className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="Tilføj opgave..."
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            className="flex-1 border p-2 rounded"
          />
          <button type="submit" className="bg-blue-600 text-white px-4 rounded hover:bg-blue-700">
            Tilføj
          </button>
        </form>

        {tasks.length === 0 ? (
          <p className="text-gray-500">Ingen opgaver endnu.</p>
        ) : (
          <ul className="divide-y border rounded bg-white">
            {tasks.map((t) => (
              <li key={t.id} className="p-3 flex flex-col gap-2 hover:bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="font-medium text-gray-800">{t.title}</div>
                  <button
                    onClick={() => handleDeleteTask(t.id)}
                    className="text-red-500 hover:text-red-700 text-sm"
                    type="button"
                  >
                    Slet
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={t.progress_pct ?? 0}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, progress_pct: v } : x)));
                    }}
                    onMouseUp={async () => {
                      await updateTask(t.id, { progress_pct: t.progress_pct ?? 0 });
                    }}
                    onTouchEnd={async () => {
                      await updateTask(t.id, { progress_pct: t.progress_pct ?? 0 });
                    }}
                    className="flex-1"
                  />
                  <div className="w-12 text-right text-sm">{t.progress_pct ?? 0}%</div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input
                    type="date"
                    value={t.follow_up_date ?? ""}
                    onChange={async (e) => {
                      await updateTask(t.id, { follow_up_date: e.target.value || null });
                      await loadProjectAndTasks();
                    }}
                    className="border p-2 rounded"
                  />

                  <input
                    type="text"
                    placeholder="Opfølgningsnote..."
                    value={t.follow_up_note ?? ""}
                    onChange={async (e) => {
                      await updateTask(t.id, { follow_up_note: e.target.value });
                      await loadProjectAndTasks();
                    }}
                    className="border p-2 rounded md:col-span-2"
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Noter / Mødelog */}
      <div className="border rounded p-4 bg-gray-50 mt-8">
        <div className="mb-4">
          <h2 className="font-semibold text-lg text-gray-700">Noter / Mødelog</h2>
          <p className="text-sm text-gray-500">Skriv direkte i boksen og gem. Lås noter når de er “færdige”. Locked noter kan eksporteres til PDF.</p>
        </div>

        {/* ÅBEN NOTE-EDITOR */}
        <div className="border rounded bg-white p-3 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-2">
            <input
              className="border p-2 rounded md:col-span-2"
              placeholder="Titel fx 'Byggemøde'"
              value={noteDraft.title}
              onChange={(e) => setNoteDraft((p) => ({ ...p, title: e.target.value }))}
            />
            <input
              type="date"
              className="border p-2 rounded md:col-span-1"
              value={noteDraft.note_date}
              onChange={(e) => setNoteDraft((p) => ({ ...p, note_date: e.target.value }))}
            />
            <div className="md:col-span-3 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => saveNewNote({ lockAfter: false })}
                className="border px-4 py-2 rounded hover:bg-gray-50"
              >
                Gem (åben)
              </button>
              <button
                type="button"
                onClick={() => saveNewNote({ lockAfter: true })}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Gem & Lås
              </button>
            </div>
          </div>

          <textarea
            className="border p-2 rounded w-full"
            rows={5}
            placeholder="Skriv mødenoter / aftaler / beslutninger..."
            value={noteDraft.body}
            onChange={(e) => setNoteDraft((p) => ({ ...p, body: e.target.value }))}
          />
          <div className="text-xs text-gray-500 mt-2">
            Tip: Brug “Gem & Lås” til byggemøder, så teksten ikke ændres bagefter.
          </div>
        </div>

        {/* LISTE */}
        {notesLoading ? (
          <p className="text-gray-500">Henter noter...</p>
        ) : notes.length === 0 ? (
          <p className="text-gray-500">Ingen gemte noter endnu.</p>
        ) : (
          <ul className="space-y-2">
            {notes.map((n) => {
              const isEditing = editingId === n.id;
              const isLocked = !!n.locked;

              return (
                <li key={n.id} className="border rounded bg-white p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm text-gray-500">
                        {n.note_date || ""} • {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="font-semibold text-gray-800">{n.title || "Note"}</div>
                        {isLocked ? (
                          <span className="text-xs px-2 py-1 rounded bg-gray-100 border text-gray-700">
                            Locked
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded bg-green-50 border text-green-700">
                            Åben
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-3 items-center">
                      {isLocked ? (
                        <button
                          type="button"
                          onClick={() => exportNoteToPDF(n)}
                          className="text-blue-600 hover:underline text-sm"
                        >
                          Eksportér PDF
                        </button>
                      ) : null}

                      {!isLocked && !isEditing ? (
                        <button
                          type="button"
                          onClick={() => startEditNote(n)}
                          className="text-blue-600 hover:underline text-sm"
                        >
                          Redigér
                        </button>
                      ) : null}

                      {!isLocked ? (
                        <button
                          type="button"
                          onClick={() => lockNote(n.id)}
                          className="text-gray-700 hover:underline text-sm"
                          disabled={savingNoteId === n.id}
                        >
                          Lås
                        </button>
                      ) : null}

                      <button
                        type="button"
                        onClick={() => removeNote(n.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Slet
                      </button>
                    </div>
                  </div>

                  <div className="mt-2">
                    {isEditing && !isLocked ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-2">
                          <input
                            className="border p-2 rounded md:col-span-2"
                            value={editDraft.title}
                            onChange={(e) => setEditDraft((p) => ({ ...p, title: e.target.value }))}
                            placeholder="Titel"
                          />
                          <input
                            type="date"
                            className="border p-2 rounded md:col-span-1"
                            value={editDraft.note_date}
                            onChange={(e) => setEditDraft((p) => ({ ...p, note_date: e.target.value }))}
                          />
                          <div className="md:col-span-3 flex gap-2 justify-end">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingId(null);
                                setEditDraft({ title: "", note_date: "", body: "" });
                              }}
                              className="border px-3 py-2 rounded hover:bg-gray-50"
                            >
                              Annullér
                            </button>
                            <button
                              type="button"
                              onClick={() => saveEditNote(n.id)}
                              className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700"
                              disabled={savingNoteId === n.id}
                            >
                              {savingNoteId === n.id ? "Gemmer..." : "Gem ændringer"}
                            </button>
                          </div>
                        </div>

                        <textarea
                          className="border p-2 rounded w-full"
                          rows={5}
                          value={editDraft.body}
                          onChange={(e) => setEditDraft((p) => ({ ...p, body: e.target.value }))}
                        />
                      </>
                    ) : (
                      <div className="whitespace-pre-wrap text-gray-800">{n.body}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Expenses modal */}
      {expensesOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeExpensesModal} />

          <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-lg p-5 mx-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Omkostninger</h3>
                <p className="text-sm text-gray-500">Registrér løbende udgifter på projektet.</p>
              </div>

              <button onClick={closeExpensesModal} className="text-gray-500 hover:text-gray-800" type="button">
                ✕
              </button>
            </div>

            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Total: <span className="font-bold">{Number(expensesTotal).toLocaleString()} kr.</span>
              </div>
            </div>

            <form onSubmit={addExpense} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4">
              <input
                className="border p-2 rounded md:col-span-1"
                placeholder="Leverandør"
                value={expenseForm.vendor}
                onChange={(e) => setExpenseForm((p) => ({ ...p, vendor: e.target.value }))}
              />
              <input
                className="border p-2 rounded md:col-span-2"
                placeholder="Beskrivelse"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm((p) => ({ ...p, description: e.target.value }))}
                required
              />
              <input
                className="border p-2 rounded md:col-span-1"
                type="number"
                placeholder="Beløb"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm((p) => ({ ...p, amount: e.target.value }))}
                required
              />
              <input
                className="border p-2 rounded md:col-span-1"
                type="date"
                value={expenseForm.expense_date}
                onChange={(e) => setExpenseForm((p) => ({ ...p, expense_date: e.target.value }))}
              />

              <button type="submit" className="md:col-span-5 bg-blue-600 text-white py-2 rounded hover:bg-blue-700">
                Tilføj omkostning
              </button>
            </form>

            <div className="border rounded overflow-hidden">
              <div className="grid grid-cols-12 bg-gray-100 text-sm font-semibold p-2">
                <div className="col-span-2">Dato</div>
                <div className="col-span-2">Leverandør</div>
                <div className="col-span-6">Beskrivelse</div>
                <div className="col-span-2 text-right">Beløb</div>
              </div>

              {expenseLoading ? (
                <div className="p-3 text-gray-500">Henter...</div>
              ) : expenseRows.length === 0 ? (
                <div className="p-3 text-gray-500">Ingen omkostninger endnu.</div>
              ) : (
                expenseRows.map((r) => (
                  <div key={r.id} className="grid grid-cols-12 p-2 border-t items-center text-sm">
                    <div className="col-span-2">{r.expense_date || ""}</div>
                    <div className="col-span-2">{r.vendor || "-"}</div>
                    <div className="col-span-6">{r.description}</div>
                    <div className="col-span-2 text-right">
                      {Number(r.amount || 0).toLocaleString()} kr.
                      <button
                        className="ml-3 text-red-500 hover:text-red-700"
                        onClick={() => removeExpense(r.id)}
                        type="button"
                      >
                        Slet
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={closeExpensesModal} className="border px-4 py-2 rounded hover:bg-gray-50" type="button">
                Luk
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
